import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, PageShell } from "@/components/app/page";
import { messagesAPI } from "@/services/api";
import { ADMIN_RANKS, hasAdminRank } from "@/utils/adminRanks";
import { getErrorMessage } from "@/utils/errorHelpers";
import { ShieldAlert, UserX } from "lucide-react";
import { toast } from "sonner";

export default function MessagesPage({ user }) {
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [participantId, setParticipantId] = useState("");
  const [audienceType, setAudienceType] = useState("participant");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [reportingId, setReportingId] = useState("");
  const [blocking, setBlocking] = useState(false);
  const isPartnerManager = hasAdminRank(user, ADMIN_RANKS.GP);
  const directParticipant = selected?.audience_type === "participant"
    ? (selected.participants || []).find((participant) => participant.id !== user?.id)
    : null;

  const loadConversations = useCallback(async () => {
    const res = await messagesAPI.listConversations();
    const rows = Array.isArray(res.data) ? res.data : [];
    setConversations(rows);
    if (!selected && rows.length > 0) setSelected(rows[0]);
  }, [selected]);

  useEffect(() => {
    loadConversations().catch(() => toast.error("Erreur chargement conversations"));
  }, [loadConversations]);

  useEffect(() => {
    if (!selected?.id) return;
    messagesAPI.getMessages(selected.id)
      .then((res) => setMessages(Array.isArray(res.data) ? res.data : []))
      .catch(() => toast.error("Erreur chargement messages"));
  }, [selected]);

  const createConversation = async (e) => {
    e.preventDefault();
    try {
      const res = await messagesAPI.createConversation({
        participant_id: audienceType === "participant" ? participantId : null,
        audience_type: audienceType,
        subject,
      });
      setParticipantId("");
      setAudienceType("participant");
      setSubject("");
      setSelected(res.data);
      await loadConversations();
    } catch (error) {
      toast.error(getErrorMessage(error, "Conversation impossible"));
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!selected?.id || !body.trim()) return;
    try {
      await messagesAPI.sendMessage(selected.id, body);
      setBody("");
      const res = await messagesAPI.getMessages(selected.id);
      setMessages(Array.isArray(res.data) ? res.data : []);
      await loadConversations();
    } catch (error) {
      toast.error(getErrorMessage(error, "Message non envoye"));
    }
  };

  const reportMessage = async (msg) => {
    const reason = window.prompt("Motif du signalement");
    if (!reason?.trim()) return;
    setReportingId(msg.id);
    try {
      await messagesAPI.report({
        target_type: "message",
        target_id: msg.id,
        reason,
        context: {
          conversation_id: selected?.id,
          sender_id: msg.sender_id,
        },
      });
      toast.success("Signalement transmis");
    } catch (error) {
      toast.error(getErrorMessage(error, "Signalement impossible"));
    } finally {
      setReportingId("");
    }
  };

  const blockDirectParticipant = async () => {
    if (!directParticipant?.id) return;
    const confirmed = window.confirm(`Bloquer ${directParticipant.full_name || directParticipant.id} dans la messagerie ?`);
    if (!confirmed) return;
    setBlocking(true);
    try {
      await messagesAPI.blockUser(directParticipant.id);
      toast.success("Utilisateur bloque");
      const res = await messagesAPI.getMessages(selected.id);
      setMessages(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      toast.error(getErrorMessage(error, "Blocage impossible"));
    } finally {
      setBlocking(false);
    }
  };

  return (
    <PageShell>
      <PageHeader title="Messagerie" description="Echanges entre partenaires, acheteurs et gestionnaires partenaires." />
      <div className="grid gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
        <Card>
          <CardHeader><CardTitle className="text-lg text-[#2A4B46]">Conversations</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={createConversation} className="space-y-3 rounded-xl border border-[#E6E4E0] p-3">
              <div>
                <Label>Destinataire</Label>
                <Select value={audienceType} onValueChange={setAudienceType}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="participant">Une personne</SelectItem>
                    {user?.role === "partner" && <SelectItem value="generation">Ma generation</SelectItem>}
                    <SelectItem value="partner_managers">Gestionnaires partenaires</SelectItem>
                    {isPartnerManager && <SelectItem value="partners">Tous les partenaires</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              {audienceType === "participant" && (
                <div><Label>ID participant</Label><Input value={participantId} onChange={(e) => setParticipantId(e.target.value.toUpperCase())} placeholder="ART243..." required /></div>
              )}
              <div><Label>Sujet</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} /></div>
              <Button type="submit" className="w-full bg-[#2A4B46] text-white hover:bg-[#1f3a36]">Demarrer</Button>
            </form>
            <div className="space-y-2">
              {conversations.map((conv) => (
                <button
                  key={conv.id}
                  onClick={() => setSelected(conv)}
                  className={`w-full rounded-xl border p-3 text-left text-sm ${selected?.id === conv.id ? "border-[#2A4B46] bg-[#F3EFE9]" : "border-[#E6E4E0]"}`}
                >
                  <p className="font-semibold text-[#2A4B46]">{conv.subject || "Conversation"}</p>
                  <p className="text-[11px] uppercase tracking-wide text-[#8A8F8C]">{formatAudience(conv.audience_type)}</p>
                  <p className="truncate text-xs text-[#5C625F]">{conv.last_message || "Aucun message"}</p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="text-lg text-[#2A4B46]">{selected?.subject || "Messages"}</CardTitle>
              {directParticipant && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={blockDirectParticipant}
                  disabled={blocking}
                  className="gap-2 border-red-200 text-red-700 hover:bg-red-50"
                >
                  <UserX size={15} /> {blocking ? "Blocage..." : "Bloquer"}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="min-h-80 space-y-3 rounded-xl bg-[#F9F7F4] p-4">
              {messages.length === 0 && <p className="text-sm text-[#5C625F]">Aucun message.</p>}
              {messages.map((msg) => (
                <div key={msg.id} className="rounded-xl bg-white p-3 text-sm shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <p className="font-semibold text-[#2A4B46]">{msg.sender_name}</p>
                    {msg.sender_id !== user?.id && (
                      <button
                        type="button"
                        onClick={() => reportMessage(msg)}
                        disabled={reportingId === msg.id}
                        className="inline-flex items-center gap-1 rounded-full border border-amber-200 px-2 py-1 text-[11px] font-semibold text-amber-700 hover:bg-amber-50 disabled:opacity-60"
                      >
                        <ShieldAlert size={12} /> Signaler
                      </button>
                    )}
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-[#1A1D1C]">{msg.body}</p>
                </div>
              ))}
            </div>
            <form onSubmit={sendMessage} className="flex flex-col gap-3">
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="Ecrire un message..." />
              <Button type="submit" disabled={!selected} className="bg-[#2A4B46] text-white hover:bg-[#1f3a36]">Envoyer</Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </PageShell>
  );
}

function formatAudience(type) {
  const labels = {
    participant: "Direct",
    generation: "Generation",
    partner_managers: "Gestionnaires",
    partners: "Partenaires",
  };
  return labels[type] || "Direct";
}
